import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, Settings, 
  ChevronLeft, Menu, LogOut, FolderTree, FileText, MessageSquare
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { NotificationBell } from './NotificationBell';
import PushNotificationToggle from './PushNotificationToggle';

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/products', icon: Package, label: 'Produits' },
  { path: '/admin/categories', icon: FolderTree, label: 'Catégories' },
  { path: '/admin/orders', icon: ShoppingCart, label: 'Commandes' },
  { path: '/admin/sales-report', icon: FileText, label: 'Rapport Ventes' },
  { path: '/admin/customers', icon: Users, label: 'Clients' },
  { path: '/admin/reviews', icon: MessageSquare, label: 'Avis Clients' },
  { path: '/admin/settings', icon: Settings, label: 'Paramètres' },
];

export const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-brand-cream flex">
      {/* Sidebar - Black with Gold accents */}
      <aside 
        className={`fixed left-0 top-0 h-full bg-brand-black text-white transition-all duration-300 z-40 ${
          sidebarCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className="p-4 border-b border-brand-gold/30">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <h1 className="font-heading text-xl text-brand-gold tracking-wide">
                Admin Panel
              </h1>
            )}
            <div className="flex items-center gap-2">
              <NotificationBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="text-brand-gold/60 hover:text-brand-gold hover:bg-brand-gold/10"
              >
                {sidebarCollapsed ? <Menu className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-2">
          {navItems.map(item => {
            const isActive = location.pathname === item.path || 
                           (item.path !== '/admin' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-200 ${
                  isActive 
                    ? 'bg-brand-gold text-brand-black font-medium' 
                    : 'text-white/70 hover:bg-brand-gold/10 hover:text-brand-gold'
                }`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!sidebarCollapsed && <span className="font-body">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-brand-gold/30">
          {/* Push Notifications Toggle */}
          {!sidebarCollapsed && (
            <div className="mb-3 px-2">
              <PushNotificationToggle />
            </div>
          )}
          <Link
            to="/"
            className={`flex items-center gap-3 px-4 py-3 rounded-sm text-brand-gold hover:bg-brand-gold/10 transition-colors mb-2`}
          >
            <ChevronLeft className="h-5 w-5" />
            {!sidebarCollapsed && <span className="font-body">Retour au site</span>}
          </Link>
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 px-4 py-3 rounded-sm text-white/60 hover:bg-brand-gold/10 hover:text-brand-gold transition-colors w-full`}
          >
            <LogOut className="h-5 w-5" />
            {!sidebarCollapsed && <span className="font-body">Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={`flex-1 transition-all duration-300 ${
          sidebarCollapsed ? 'ml-20' : 'ml-64'
        }`}
      >
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
